import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lock, TrendingUp, Store, Sparkles, CheckCircle2, Users } from 'lucide-react';
import { useLocale, PRICING } from '@/contexts/LocaleContext';

const ProPaywall = ({ isOpen, onClose, feature = 'esta función', reason = 'default' }) => {
  const navigate = useNavigate();
  const { isUSA, currency, region } = useLocale();
  const pricing = PRICING[region]?.pro || { price: 599 };

  const handleActivate = () => {
    onClose();
    navigate('/dashboard/subscription');
  };

  // Different messages based on the reason for showing paywall
  const messages = {
    product_limit: {
      title: isUSA ? "You're growing! Time to scale up" : "¡Estás creciendo! Es hora de escalar",
      subtitle: isUSA 
        ? "You've reached 5 products. Upgrade to PRO for unlimited products and more clients."
        : "Llegaste a 5 productos. Actualiza a PRO para productos ilimitados y más clientes."
    },
    store: {
      title: isUSA ? "Unlock your online store" : "Desbloquea tu tienda en línea",
      subtitle: isUSA 
        ? "Create your professional store and start receiving orders directly."
        : "Crea tu tienda profesional y empieza a recibir pedidos directamente."
    },
    texvision: {
      title: isUSA ? "Create professional images" : "Crea imágenes profesionales",
      subtitle: isUSA 
        ? "Use AI to generate stunning product photos that attract more buyers."
        : "Usa IA para generar fotos de productos impactantes que atraen más compradores."
    },
    inquiries: {
      title: isUSA ? "You're getting traction!" : "¡Estás generando interés!",
      subtitle: isUSA 
        ? "You've received inquiries. Upgrade to PRO to continue connecting with buyers."
        : "Has recibido consultas. Actualiza a PRO para seguir conectando con compradores."
    },
    default: {
      title: isUSA ? "Upgrade to get more clients" : "Actualiza para conseguir más clientes",
      subtitle: isUSA 
        ? `To use ${feature}, you need TexHub PRO.`
        : `Para usar ${feature}, necesitas TexHub PRO.`
    }
  };

  const currentMessage = messages[reason] || messages.default;

  const benefits = isUSA ? [
    'Unlimited products in your catalog',
    'Priority in search results',
    'Your own online store',
    'TexVision AI for product images',
    'Featured supplier badge'
  ] : [
    'Productos ilimitados en tu catálogo',
    'Prioridad en resultados de búsqueda',
    'Tu propia tienda en línea',
    'TexVision AI para imágenes de productos',
    'Badge de proveedor destacado'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="pro-paywall-modal">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center" data-testid="pro-paywall-lock-icon">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl" data-testid="pro-paywall-title">
            {currentMessage.title}
          </DialogTitle>
        </DialogHeader>

        <div className="text-center mb-4">
          <p className="text-slate-600">
            {currentMessage.subtitle}
          </p>
        </div>

        {/* Price Banner */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 text-center">
          <div className="text-3xl font-bold text-slate-900">
            ${pricing.price} <span className="text-base font-normal text-slate-500">{currency}/mes</span>
          </div>
          <p className="text-slate-500 text-sm mt-1">
            {isUSA ? 'Cancel anytime' : 'Cancela cuando quieras'}
          </p>
        </div>

        {/* Benefits */}
        <ul className="space-y-2 mb-6">
          {benefits.map((benefit, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              {benefit}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <Button 
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-5"
          onClick={handleActivate}
          data-testid="pro-paywall-cta-button"
        >
          <Users className="w-5 h-5 mr-2" />
          {isUSA ? 'Start getting more clients' : 'Empieza a conseguir más clientes'}
        </Button>

        <button 
          onClick={onClose}
          className="w-full text-slate-500 text-sm mt-2 hover:text-slate-700"
          data-testid="pro-paywall-dismiss-button"
        >
          {isUSA ? 'Maybe later' : 'Quizás después'}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default ProPaywall;
